import { useState, useEffect, useCallback } from 'react';
import apiService from '../../shared/services/ApiService';
import '../../css/HistoryDashboard.css';

const HistoryDashboard = ({ sectorId, updateHistory }) => {
    const [historyData, setHistoryData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('all'); // all, status_change, coordinate_completed, sector_completed
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [selectedImage, setSelectedImage] = useState(null);

    // Cargar historial
    const fetchHistory = useCallback(async () => {
        try {
            setLoading(true);
            const response = await apiService.get(`/api/sectores/${sectorId}/history`);
             console.log('📜 Historial cargado:', response);
            // Ordenar por fecha más reciente primero
            const sortedHistory = response.data.data.history.sort((a, b) => 
                new Date(b.date) - new Date(a.date)
            );
            
            setHistoryData(sortedHistory);
        } catch (error) {
            console.error('Error al cargar historial:', error);
        } finally {
            setLoading(false);
        }
    }, [sectorId]);

    useEffect(() => {
        if (sectorId) {
            fetchHistory();
        }
    }, [sectorId, updateHistory, fetchHistory]);

    // Filtrar historial por tipo
    const filteredHistory = historyData.filter(item => {
        if (filterType === 'all') return true;
        return item.type === filterType;
    });

    // Toggle expandir/colapsar item
    const toggleExpand = (itemId) => {
        const newExpanded = new Set(expandedItems);
        if (newExpanded.has(itemId)) {
            newExpanded.delete(itemId);
        } else {
            newExpanded.add(itemId);
        }
        setExpandedItems(newExpanded);
    };

    // Obtener icono según tipo
    const getTypeIcon = (type) => {
        switch (type) {
            case 'status_change':
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                    </svg>
                );
            case 'coordinate_completed':
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                    </svg>
                );
            case 'sector_completed':
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                );
            default:
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                    </svg>
                );
        }
    };

    // Obtener color según tipo
    const getTypeColor = (type) => {
        switch (type) {
            case 'status_change':
                return '#3498db';
            case 'coordinate_completed':
                return '#2ecc71';
            case 'sector_completed':
                return '#9b59b6';
            default:
                return '#95a5a6';
        }
    };

    // Obtener título según tipo
    const getTypeTitle = (type) => {
        switch (type) {
            case 'status_change':
                return 'Cambio de Estado';
            case 'coordinate_completed':
                return 'Punto Completado';
            case 'sector_completed':
                return 'Sector Completado';
            default:
                return 'Evento';
        }
    };

    // Formatear fecha
    const formatDate = (date) => {
        return new Date(date).toLocaleString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="history-loading">
                <div className="spinner"></div>
                <p>Cargando historial...</p>
            </div>
        );
    }

    if (historyData.length === 0) {
        return (
            <div className="history-empty">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p>No hay historial disponible para este sector</p>
            </div>
        );
    }

    return (
        <div className="history-dashboard">
            {/* Filtros */}
            <div className="history-filters">
                <button
                    className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
                    onClick={() => setFilterType('all')}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="4" y1="21" x2="4" y2="14" />
                        <line x1="4" y1="10" x2="4" y2="3" />
                        <line x1="12" y1="21" x2="12" y2="12" />
                        <line x1="12" y1="8" x2="12" y2="3" />
                        <line x1="20" y1="21" x2="20" y2="16" />
                        <line x1="20" y1="12" x2="20" y2="3" />
                    </svg>
                    Todos ({historyData.length})
                </button>
                <button
                    className={`filter-btn ${filterType === 'status_change' ? 'active' : ''}`}
                    onClick={() => setFilterType('status_change')}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                    </svg>
                    Estados ({historyData.filter(h => h.type === 'status_change').length})
                </button>
                <button
                    className={`filter-btn ${filterType === 'coordinate_completed' ? 'active' : ''}`}
                    onClick={() => setFilterType('coordinate_completed')}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                    </svg>
                    Puntos ({historyData.filter(h => h.type === 'coordinate_completed').length})
                </button>
                <button
                    className={`filter-btn ${filterType === 'sector_completed' ? 'active' : ''}`}
                    onClick={() => setFilterType('sector_completed')}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                    Completados ({historyData.filter(h => h.type === 'sector_completed').length})
                </button>
            </div>

            {/* Timeline */}
            <div className="history-timeline">
                {filteredHistory.length === 0 ? (
                    <div className="history-empty">
                        <p>No hay eventos de este tipo</p>
                    </div>
                ) : (
                    filteredHistory.map((item, index) => {
                        const isExpanded = expandedItems.has(item._id);
                        const typeColor = getTypeColor(item.type);

                        return (
                            <div key={item._id} className="timeline-item">
                                <div className="timeline-marker" style={{ backgroundColor: typeColor }}>
                                    {getTypeIcon(item.type)}
                                </div>
                                <div className="timeline-content">
                                    <div className="timeline-header" onClick={() => toggleExpand(item._id)}>
                                        <div className="timeline-title">
                                            <span className="type-badge" style={{ backgroundColor: typeColor }}>
                                                {getTypeTitle(item.type)}
                                            </span>
                                            {item.status && (
                                                <span className={`status-badge status-${item.status.toLowerCase()}`}>
                                                    {item.status}
                                                </span>
                                            )}
                                        </div>
                                        <div className="timeline-meta">
                                            <span className="timeline-date">{formatDate(item.date)}</span>
                                            {item.completedBy && (
                                                <span className="timeline-user">
                                                    Por: {item.completedBy.username || item.completedBy.email || 'Usuario'}
                                                </span>
                                            )}
                                            <button className="expand-btn">
                                                <svg 
                                                    xmlns="http://www.w3.org/2000/svg" 
                                                    width="16" 
                                                    height="16" 
                                                    viewBox="0 0 24 24" 
                                                    fill="none" 
                                                    stroke="currentColor" 
                                                    strokeWidth="2"
                                                    style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s ease' }}
                                                >
                                                    <polyline points="6 9 12 15 18 9" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Comentario siempre visible */}
                                    {item.comment && (
                                        <div className="timeline-comment">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                            </svg>
                                            {item.comment}
                                        </div>
                                    )}

                                    {/* Detalles expandibles */}
                                    {isExpanded && (
                                        <div className="timeline-details">
                                            {/* Detalles de Coordenada */}
                                            {item.type === 'coordinate_completed' && item.coordinateDetails && (
                                                <div className="detail-section">
                                                    <h5>📍 Detalles del Punto {item.coordinateDetails.coordinateIndex + 1}</h5>
                                                    <div className="detail-grid">
                                                        <div className="detail-item">
                                                            <span className="detail-label">Latitud:</span>
                                                            <span className="detail-value">{item.coordinateDetails.latitude.toFixed(6)}</span>
                                                        </div>
                                                        <div className="detail-item">
                                                            <span className="detail-label">Longitud:</span>
                                                            <span className="detail-value">{item.coordinateDetails.longitude.toFixed(6)}</span>
                                                        </div>
                                                    </div>

                                                    {item.coordinateDetails.workDone && (
                                                        <div className="detail-box success">
                                                            <h6>✅ Trabajo Realizado</h6>
                                                            <p>{item.coordinateDetails.workDone}</p>
                                                        </div>
                                                    )}

                                                    {item.coordinateDetails.issues && (
                                                        <div className="detail-box warning">
                                                            <h6>⚠️ Problemas Encontrados</h6>
                                                            <p>{item.coordinateDetails.issues}</p>
                                                        </div>
                                                    )}

                                                    {item.coordinateDetails.photos && item.coordinateDetails.photos.length > 0 && (
                                                        <div className="detail-photos">
                                                            <h6>📷 Fotografías ({item.coordinateDetails.photos.length})</h6>
                                                            <div className="photos-grid-mini">
                                                                {item.coordinateDetails.photos.map((photo, idx) => (
                                                                    <img
                                                                        key={idx}
                                                                        src={photo}
                                                                        alt={`Foto ${idx + 1}`}
                                                                        className="photo-mini"
                                                                        onClick={() => setSelectedImage(photo)}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Detalles de Sector Completado */}
                                            {item.type === 'sector_completed' && item.completionDetails && (
                                                <div className="detail-section">
                                                    <h5>🎉 Resumen de Completación</h5>
                                                    <div className="completion-stats">
                                                        <div className="stat-box">
                                                            <span className="stat-number">{item.completionDetails.completedPoints}</span>
                                                            <span className="stat-label">Puntos Completados</span>
                                                        </div>
                                                        <div className="stat-box">
                                                            <span className="stat-number">{item.completionDetails.duration}</span>
                                                            <span className="stat-label">Minutos</span>
                                                        </div>
                                                    </div>

                                                    {item.completionDetails.allCoordinates && item.completionDetails.allCoordinates.length > 0 && (
                                                        <div className="coordinates-summary">
                                                            <h6>📋 Puntos Verificados</h6>
                                                            <div className="coordinates-list">
                                                                {item.completionDetails.allCoordinates.map((coord, idx) => (
                                                                    <div key={idx} className="coordinate-summary-item">
                                                                        <span className="coord-number">#{coord.index + 1}</span>
                                                                        <div className="coord-info">
                                                                            {coord.comment && (
                                                                                <p className="coord-comment">{coord.comment}</p>
                                                                            )}
                                                                            {coord.workDone && (
                                                                                <p className="coord-work">✅ {coord.workDone}</p>
                                                                            )}
                                                                            {coord.issues && (
                                                                                <p className="coord-issues">⚠️ {coord.issues}</p>
                                                                            )}
                                                                        </div>
                                                                        {coord.photos && coord.photos.length > 0 && (
                                                                            <span className="coord-photos">
                                                                                📷 {coord.photos.length}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Brigada asignada */}
                                            {item.brigadeAssigned && (
                                                <div className="detail-item">
                                                    <span className="detail-label">Brigada:</span>
                                                    <span className="detail-value">{item.brigadeAssigned.name || 'N/A'}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Modal de imagen */}
            {selectedImage && (
                <div className="image-modal" onClick={() => setSelectedImage(null)}>
                    <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="image-modal-close" onClick={() => setSelectedImage(null)}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="18" y1="6" x2="6" y2="18" />
                                <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                        </button>
                        <img src={selectedImage} alt="Vista ampliada" />
                    </div>
                </div>
            )}
        </div>
    );
};

export default HistoryDashboard;